import { useCallback, useMemo } from 'react'
import { isNearby } from '@shared/regions'
import { useHomeRegion } from './useHomeRegion'
import { useEventRegions } from './useEventRegions'
import { useRegionEvents } from './useRegionEvents'
import { attendingEventIds, useEventsByIds } from './useEventsByIds'
import type { Event, EventAttendanceStatus } from '../types'

/**
 * Everything a discovery page needs for the user's chosen region: the region,
 * the events to show, and the scope predicate.
 *
 * `events` is the region's events (from `useRegionEvents`) plus the viewer's
 * own going/interested ones wherever they are, since a festival you're going
 * to stays in scope from any region. Pass `includePast` for pages with a
 * "Previous" list, and `type` for a page that shows only one type.
 *
 * Proximity decides whether an event is shown, never its position — callers
 * keep their existing sort, so a list never reorders in a way the user can't
 * explain.
 */
export function useRegionalEvents(
  eventStatuses: Map<string, EventAttendanceStatus>,
  { includePast = false, type }: { includePast?: boolean; type?: Event['type'] } = {},
) {
  const { regionId, region, source, setRegion, isEverywhere } = useHomeRegion()
  const upcoming = useRegionEvents(regionId, { type })
  const past = useRegionEvents(regionId, { window: 'past', type, enabled: includePast })
  const ownIds = useMemo(() => attendingEventIds(eventStatuses), [eventStatuses])
  const own = useEventsByIds(ownIds)

  const events = useMemo(() => {
    const byId = new Map<string, Event>()
    for (const e of [...upcoming.events, ...past.events, ...own.events]) byId.set(e.id, e)
    return [...byId.values()]
  }, [upcoming.events, past.events, own.events])

  const { regions, loading } = useEventRegions(events)

  const isNear = useCallback(
    (event: Event) => isNearby(regionId, regions.get(event.id), event.type),
    [regionId, regions],
  )

  /**
   * Whether an event belongs on a discovery surface for the chosen region.
   * With a region selected, only events near it show — parties strictly, while
   * festivals also reach drivable regions (see `isNearby`) and a festival you
   * are going to or interested in shows wherever it is: you travel for those.
   * A party elsewhere stays out even if you RSVP'd, because switching region
   * means you want that city's nights, not yours.
   */
  const inScope = useCallback(
    (event: Event, status?: EventAttendanceStatus) =>
      isEverywhere ||
      isNear(event) ||
      (event.type === 'festival' && (status === 'going' || status === 'interested')),
    [isEverywhere, isNear],
  )

  return {
    regionId,
    region,
    source,
    setRegion,
    isEverywhere,
    isNear,
    inScope,
    events,
    /** Nothing to render yet. */
    eventsLoading: upcoming.loading,
    /** Some listener still owes its first snapshot — hold any freeze. */
    eventsPending: upcoming.pending || past.pending || own.pending,
    eventsError: upcoming.error ?? past.error ?? own.error,
    regionsLoading: loading,
  }
}
